<template>
  <article>
    <AppBanner
      title="Orders"
      title-alignment="text-left flex-row max-w-[1600px]"
    />

    <div class="max-w-[1600px] mx-auto container-padding flex flex-col my-8">
      <div class="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <!-- Search -->
        <section class="flex w-fit">
          <div class="w-[250px] md:w-[200px] lg:w-[300px]">
            <input
              v-model="searchParams.search"
              type="text"
              class="relative border-gray focus:border-secondary focus:ring-0 rounded-r-none border-r-0 py-2 w-full"
              placeholder="Search for order"
              @keyup.enter="loadOrders"
            >
          </div>

          <AppDropdown
            class="admin-dropdown context-dropdown w-38 md:w-44 whitespace-nowrap"
            :trigger-text="searchByText || 'Context'"
          >
            <AppDropdownItem @click="searchParams.searchBy = null; searchByText = 'Context'">
              All
            </AppDropdownItem>

            <template
              v-for="context in orderSearchContext"
              :key="context.value"
            >
              <AppDropdownItem @click="searchParams.searchBy = context.value; searchByText = context.label">
                {{ context.label }}
              </AppDropdownItem>
            </template>
          </AppDropdown>
        </section>

        <section class="flex gap-2 sm:gap-4 w-full">
          <!-- status drop-down -->
          <AppDropdown
            class="admin-dropdown whitespace-nowrap w-40 md:w-46"
            :trigger-text="statusText || 'Status'"
          >
            <AppDropdownItem @click="searchParams.status = null; statusText = 'Status'">
              All
            </AppDropdownItem>

            <template
              v-for="status in OrderStatuses"
              :key="status.value"
            >
              <AppDropdownItem @click="searchParams.status = status.value; statusText = status.label">
                {{ status.label }}
              </AppDropdownItem>
            </template>
          </AppDropdown>

          <AppButton
            class="btn btn-solid btn-secondary rounded rounded-e h-10 lg:h-11 text-sm lg:text-base w-fit"
            @click="loadOrders"
          >
            Search
          </AppButton>
        </section>
      </div>

      <!-- table -->
      <AppTable
        :loading="loading"
        :if-looped-items-exist="orders?.length > 0"
        class="order-table max-w-[1600px] mx-auto"
      >
        <template #headings>
          <th>Order Number</th>
          <th>Date</th>
          <th>Email</th>
          <th>Phone Number</th>
          <th>Customer</th>
          <th>Status</th>
          <th>Amount</th>
        </template>

        <template #rows>
          <tr
            v-for="order in orders"
            :key="order.id"
          >
            <td>
              <AppLink
                :to="{ name: 'app_order_details', params: { order: order.id } }"
                class="text-secondary underline"
              >
                {{ order?.orderNumber || 0 }}
              </AppLink>
            </td>
            <td>{{ dateFormatter.formatShortDateAndTime(order?.createdAt) }}</td>
            <td>{{ order?.email }}</td>
            <td>{{ order?.deliveryDetails?.phoneNumber || '-' }}</td>
            <td>{{ customerName(order.deliveryDetails.firstName, order.deliveryDetails.lastName) }}</td>
            <td>
              <p
                class="rounded px-2 py-0.5 font-medium whitespace-nowrap m-0 md:static text-[15px] w-fit h-fit text-black bg-gray-light"
                :class="order?.status === 'Awaiting Payment' ? 'text-red-600 bg-red-200' :
                  order?.status === 'Processing' ? '!bg-[#A2D5FF]' : ''"
              >
                {{ order?.status || '-' }}
              </p>
            </td>
            <td class="text-right w-1">
              R{{ order?.totalCost || '-' }}
            </td>
          </tr>
        </template>
      </AppTable>

      <AdminPagination
        v-if="!loading"
        target-id="topOfPage"
        :items-per-page="paginationData?.itemsPerPage"
        :current-page="paginationData?.page"
        :total-items="paginationData?.total"
        @change="handlePageChange"
      />
    </div>
  </article>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref } from 'vue';

import { AppBanner, AppButton, AppDropdown, AppDropdownItem, AppLink, AppTable } from '@/components/app';
import { AdminPagination } from '@/components/app/admin';

import { useHttp, useDateFormatter } from '@/composables';
import { OrderStatuses } from '@/const';

const dateFormatter = useDateFormatter();

const errors = ref(null);
const orders = ref([]);
const loading = ref(false);

let searchParams = {
  page: 1,
  itemsPerPage: 10,
  searchBy: null,
  search: null,
  status: null,
};

const paginationData = ref({
  page: 1,
  itemsPerPage: 10,
  total: 0,
});

const loadOrders = async () => {
  loading.value = true;

  const response = await useHttp().get('/orders', searchParams);

  if (response.statusCode === HttpStatusCode.Ok) {
    orders.value = response.data.items;

    paginationData.value = response.data.pagination;
  } else {
    errors.value = response.errors;
  }

  loading.value = false;
};

loadOrders();

const handlePageChange = page => {
  searchParams.page = page;

  loadOrders();
};

const searchByText = ref(null);
const statusText = ref(null);

const orderSearchContext = [
  { label: 'Order Number', value: 'orderNumber' },
  { label: 'Customer Name', value: 'customerName' },
  { label: 'Email Address', value: 'emailAddress' },
  { label: 'Event Name', value: 'eventName' },
];

const customerName = (firstName, lastName) => {
  if (firstName && lastName) {
    return firstName + ' ' + lastName;
  } else if (firstName && !lastName) {
    return firstName;
  } else if (!firstName && lastName) {
    return lastName;
  } else {
    return '-';
  }
};

</script>
